import videojs from 'video.js';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import mixitup from 'mixitup';
import mixitupMultifilter from '../vendor/mixitup/mixitup-multifilter.min.js';
import microModal from 'micromodal';

mixitup.use(mixitupMultifilter);

gsap.registerPlugin(ScrollTrigger);

const Shop = {
  init: function () {
    //  console.log('shop initialized');

    // Add loading state to the body
    document.body.classList.add('c7-loading');

    // Check if .shop-all-collection exists
    const shopAllCollection = document.querySelector('.shop-all-collection');

    if (shopAllCollection) {
      // Run these functions only if .shop-all-collection exists
      this.setupHeroVideo();
      this.waitForFilterAndContent();
      this.fetchProductInfo();
      // Wait for Commerce7 to load the collection
      this.waitForC7ProductsToLoad();
    }

    // These functions will run regardless of .shop-all-collection presence
    this.insertCollectionTitle();
    this.setupProductCardHover();

    setTimeout(() => {
      console.log('🚀 Initial setHeroHeight call');
      this.setHeroHeight();
    }, 300);

    // Debounce the resize handler
    if (typeof this.setHeroHeight === 'function') {
      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          console.log('📐 Resize triggered setHeroHeight');
          this.setHeroHeight();
        }, 250);
      });
    }

    // Add this line to call the new function
    this.setupHeroScrollMessage();

    //  this.initHeroHeight();
  },

  setupHeroVideo: function () {
    const videoElement = document.querySelector('.shop-all-hero__video');
    if (!videoElement) return;

    // Get the video source URL
    const videoSrc = videoElement.getAttribute('data-src');

    // Determine video type based on file extension
    const isHLS = videoSrc.includes('.m3u8');
    const videoType = isHLS ? 'application/x-mpegURL' : 'video/mp4';

    this.videoPlayer = videojs(videoElement, {
      autoplay: true,
      fill: true,
      loop: true,
      muted: true,
      poster: videoElement.getAttribute('poster'),
    });

    // Set the video source with appropriate type
    this.videoPlayer.src({
      type: videoType,
      src: videoSrc,
    });

    // Set hero height after video is set up with a short delay
    setTimeout(() => {
      this.setHeroHeight();
    }, 100);

    // Pause video when it's not in viewport
    ScrollTrigger.create({
      trigger: videoElement,
      start: 'top bottom',
      end: 'bottom top',
      onEnter: () => this.videoPlayer.play(),
      onEnterBack: () => this.videoPlayer.play(),
      onLeave: () => this.videoPlayer.pause(),
      onLeaveBack: () => this.videoPlayer.pause(),
    });
  },

  fetchProductInfo: function () {
    const productElement = document.querySelector('.c7-product-info');
    if (!productElement) return;

    const productSlug = productElement.getAttribute('data-product-slug');
    if (!productSlug) return;

    fetch(`https://api.commerce7.com/v1/product/slug/${productSlug}/for-web`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'tenant': 'rombauer-vineyards', // Replace with your Commerce7 tenant name
      },
    })
      .then((response) => response.json())
      .then((data) => {
        window.affinity = data;
        const imageElement = productElement.querySelector('.c7-product-image');
        const vintageElement = productElement.querySelector('.c7-product-vintage');
        const titleElement = productElement.querySelector('.c7-product-title');
        const linkElement = productElement.querySelector('.c7-product-link');
        const badgeElement = productElement.querySelector('.c7-product-badge');

        if (imageElement && data.images && data.images.length > 0) {
          // Create main product image
          const img = document.createElement('img');
          img.src = data.images[1].src;
          img.alt = data.title;
          img.classList.add('product-main-image');

          // Create shadow-only image
          const shadowImg = document.createElement('img');
          shadowImg.src = data.images[1].src;
          shadowImg.alt = '';
          shadowImg.classList.add('product-shadow-image');

          // Append both images to the container
          imageElement.appendChild(shadowImg);
          imageElement.appendChild(img);
        }

        if (vintageElement) {
          // Extract vintage from the title
          const vintageMatch = data.title.match(/\b\d{4}\b/);
          if (vintageMatch) {
            vintageElement.textContent = vintageMatch[0];
          }
        }

        if (titleElement) {
          // Remove the year and bottle size from the title
          let cleanTitle = data.title.replace(/\b\d{4}\b/, '').trim();
          cleanTitle = cleanTitle.replace(/\s+\d+(\.\d+)?[mLl]+$/, '').trim();
          titleElement.textContent = cleanTitle;
        }

        if (linkElement) {
          linkElement.href = `/product/${data.slug}`;
        }

        // Add product badge metadata if it exists
        if (badgeElement && data.metaData && data.metaData['product-badge']) {
          badgeElement.textContent = data.metaData['product-badge'] + '!';
          badgeElement.classList.add(data.metaData['product-badge'].toLowerCase().replace(/\s+/g, '-'));
        }
      })
      .catch((error) => console.error('Error fetching product info:', error));
  },

  setupMixItUp: function (containerEl) {
    if (!containerEl) return;

    const mixer = mixitup(containerEl, {
      selectors: {
        target: '.product-card', //.product-card, .cta-card
      },
      animation: {
        duration: 350,
      },
      callbacks: {
        onMixStart: function (state) {
          const ctaCards = containerEl.querySelectorAll('.cta-card');
          const isDefaultState = state.activeFilter.selector === '.mix';

          ctaCards.forEach((card) => {
            card.style.display = isDefaultState ? '' : 'none';
          });
        },
        onMixEnd: function (state) {
          const ctaCards = containerEl.querySelectorAll('.cta-card');

          console.log(state);

          if (state.totalHide == 0) {
            ctaCards.forEach((card) => {
              card.style.display = '';
            });
          }
        },
      },
      multifilter: {
        enable: true,
      },
    });

    // Setup custom select functionality
    this.setupCustomSelects(mixer);
  },

  setupCustomSelects: function (mixer) {
    const customSelects = document.querySelectorAll('.custom-select');

    function closeAllSelects(exceptThis) {
      customSelects.forEach((select) => {
        if (select !== exceptThis) {
          const items = select.querySelector('.select-items');
          const selected = select.querySelector('.select-selected');
          items.classList.add('select-hide');
          selected.classList.remove('select-arrow-active');
        }
      });
    }

    customSelects.forEach((select) => {
      const selected = select.querySelector('.select-selected');
      const items = select.querySelector('.select-items');

      selected.addEventListener('click', function (e) {
        e.stopPropagation();
        closeAllSelects(this.parentElement);
        this.classList.toggle('select-arrow-active');
        items.classList.toggle('select-hide');
      });

      const options = items.querySelectorAll('div');
      options.forEach((option) => {
        option.addEventListener('click', function (e) {
          e.stopPropagation();
          selected.textContent = this.textContent;
          selected.classList.remove('select-arrow-active');
          items.classList.add('select-hide');

          const filterValue = this.getAttribute('data-filter');
          const filterGroup = select.getAttribute('data-filter-group');

          mixer.setFilterGroupSelectors(filterGroup, filterValue);
          mixer.parseFilterGroups();
        });
      });
    });

    document.addEventListener('click', closeAllSelects);

    ///

    gsap.set('.shop-all-filter', { autoAlpha: 1 });
  },

  waitForFilterAndContent: function () {
    const filterContainer = document.querySelector('.shop-all--filter__cntr');
    const gridContainer = document.querySelector('.shop-all-collection__grid');

    if (!filterContainer || !gridContainer) {
      // If elements are not found, retry after a short delay
      setTimeout(() => this.waitForFilterAndContent(), 100);
      return;
    }

    // Check if MixItUp has finished initializing
    if (typeof mixer === 'undefined' || !mixer.isMixing()) {
      this.setupStickyFilter();
    } else {
      // If MixItUp is still initializing, wait for it to finish
      document.addEventListener('mixEnd', () => this.setupStickyFilter(), { once: true });
    }

    // Listen for dynamic content changes (e.g., from Commerce7)
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          // Refresh ScrollTrigger when new content is added
          ScrollTrigger.refresh();
        }
      });
    });

    observer.observe(gridContainer, { childList: true, subtree: true });
  },

  setupStickyFilter: function () {
    const filterContainer = document.querySelector('.shop-all--filter__cntr');
    const gridContainer = document.querySelector('.shop-all-collection__grid');

    if (!filterContainer || !gridContainer) return;

    ScrollTrigger.matchMedia({
      '(min-width: 1024px)': () => {
        ScrollTrigger.create({
          trigger: filterContainer,
          start: 'top top',
          endTrigger: gridContainer,
          end: 'bottom top',
          pin: true,
          pinSpacing: false,
          pinType: 'fixed',
          onRefresh: (self) => {
            // Adjust the end point based on the current height of the grid container
            self.end = gridContainer.offsetHeight + gridContainer.offsetTop - window.innerHeight;
          },
        });
      },
    });

    // Refresh ScrollTrigger after a short delay to ensure all content is properly sized
    setTimeout(() => ScrollTrigger.refresh(), 100);
  },

  setupProductCardHover: function () {
    console.log('Setting up product card hover');

    // Use a MutationObserver to wait for product cards to be added to the DOM
    const observer = new MutationObserver((mutations, obs) => {
      const productCards = document.querySelectorAll('.product-card');
      if (productCards.length > 0) {
        console.log(`Found ${productCards.length} product cards`);
        obs.disconnect(); // Stop observing once we find product cards
        this.processProductCards(productCards);
      }
    });

    // Start observing the document body for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  },

  processProductCards: function (productCards) {
    productCards.forEach((card, index) => {
      // First, check if we need to replace the image container contents
      const imageContainer = card.querySelector('.product-card__image-container');
      const existingImage = imageContainer?.querySelector('.product-card__image-container .product-card__image');
      const metadata2 = card.querySelector('.product-metadata-2');

      // If there's no existing image but we have metadata-2 content, replace the container contents
      if (imageContainer && !existingImage && metadata2) {
        const newImageContent = metadata2.querySelector('.image-slot-1');
        if (newImageContent) {
          // Clear existing container content
          imageContainer.innerHTML = '';
          // Clone and append the new content
          const newContent = newImageContent.cloneNode(true);
          newContent.classList.add('product-card__image');
          imageContainer.appendChild(newContent);
        }
      }

      // Remove "Gift:" prefix from title
      const titleElement = card.querySelector('.product-card__title');
      if (titleElement && titleElement.textContent) {
        // Clean up the text: remove extra whitespace and newlines first
        let text = titleElement.textContent
          .replace(/[\n\r]+/g, '') // Remove newlines
          .replace(/\s+/g, ' ') // Replace multiple spaces with single space
          .trim(); // Remove leading/trailing whitespace

        // Remove "Gift:" prefix
        text = text.replace(/^Gift:\s*/i, '').trim();

        // Set the cleaned text
        titleElement.textContent = text;
      }

      // Continue with existing processing...
      const bottleImage = card.querySelector('.product-card__image:not(.product-card__image--shadow) img');
      const backgroundElement = card.querySelector('.product-card__background');
      const metadataElement = card.querySelector('.product-metadata');
      const vintageElement = card.querySelector('.product-card__vintage');
      const variantPriceTitleElement = card.querySelector('.c7-product__variant__price__title');
      const badgeElement = card.querySelector('.product-card__badge');

      // Process card classes for MixItUp
      const cardClasses = card.className;
      console.log(cardClasses);
      const pipedContent = cardClasses.match(/\|\|(.*?)\|\|/);
      if (pipedContent && pipedContent[1]) {
        const categories = pipedContent[1]
          .split('|')
          .map((category) => category.trim())
          .filter((category) => category && category !== 'null')
          .map((category) => category.toLowerCase().replace(/\s+/g, '-').replace(/,/g, ' '));

        // Reset the card's class to just "product-card mix"
        card.className = 'product-card mix';

        // Add categories as classes
        categories.forEach((category) => {
          // Split by space to handle multiple categories that were comma-separated
          category.split(' ').forEach((subCategory) => {
            if (subCategory) {
              // Only add if not empty
              card.classList.add(subCategory);
            }
          });
        });
      }

      if (imageContainer && bottleImage && backgroundElement && metadataElement && titleElement) {
        // console.log(`Card ${index + 1} has all required elements`);

        // console.log(card.className); // For debugging

        // Process badge
        if (badgeElement && badgeElement.textContent.trim()) {
          let badgeText = badgeElement.textContent.trim();
          badgeElement.textContent = badgeText + '!';
          let badgeClass = badgeText.toLowerCase().replace(/\s+/g, '-');
          badgeElement.classList.add(badgeClass);
        }

        // Extract vintage and clean title
        if (titleElement.textContent) {
          let fullTitle = titleElement.textContent.trim();

          // Remove everything before the pipe character (if it exists)
          fullTitle = fullTitle.split('|').pop().trim();

          // Extract vintage (assuming it's a 4-digit year)
          const vintageMatch = fullTitle.match(/\b(19|20)\d{2}\b/);
          const vintage = vintageMatch ? vintageMatch[0] : '';

          // Remove vintage from title
          let cleanTitle = fullTitle.replace(vintage, '').trim();

          // Remove bottle size from the end of the title
          // cleanTitle = cleanTitle.replace(/\s+\d+(\.\d+)?[mLl]+$/, '').trim();

          // Update vintage element
          if (vintageElement && vintage) {
            vintageElement.textContent = vintage;
          }

          // Update title element
          titleElement.textContent = cleanTitle;
        }

        // Add parentheses to variant price title
        if (variantPriceTitleElement && variantPriceTitleElement.textContent.trim()) {
          variantPriceTitleElement.textContent = `(${variantPriceTitleElement.textContent.trim()})`;
        }

        const hasBackgroundImage = this.setBackgroundImage(backgroundElement, metadataElement, index);
        if (hasBackgroundImage) {
          this.setupCrossfadeEffect(imageContainer, bottleImage, backgroundElement);
        }
      } else {
        console.log(`Card ${index + 1} is missing required elements`);
      }
    });

    // After processing all cards, initialize MixItUp
    const containerEl = document.querySelector('.shop-all-inner-cntr');
    this.setupMixItUp(containerEl);
  },

  setBackgroundImage: function (backgroundElement, metadataElement, cardIndex) {
    //  console.log(`Setting background image for card ${cardIndex + 1}`);
    try {
      const metadataContent = metadataElement.textContent.trim();
      if (!metadataContent) {
        //    console.log(`No metadata content found for card ${cardIndex + 1}`);
        return false;
      }

      const metadata = JSON.parse(metadataContent);
      // console.log('Parsed metadata:', metadata);
      const mediaShowcase = metadata['media-showcase'];

      if (mediaShowcase && mediaShowcase['plp-bkgrd-image'] && mediaShowcase['plp-bkgrd-image'].src) {
        //   console.log(`Background image URL found: ${mediaShowcase['plp-bkgrd-image'].src}`);
        backgroundElement.style.backgroundImage = `url(${mediaShowcase['plp-bkgrd-image'].src})`;
        //   console.log(`Background image set for card ${cardIndex + 1}`);
        return true;
      } else {
        //   console.log(`No valid background image URL found for card ${cardIndex + 1}`);
        return false;
      }
    } catch (error) {
      console.error(`Error processing metadata for card ${cardIndex + 1}:`, error);
      console.log('Metadata content:', metadataElement.textContent);
      return false;
    }
  },

  setupCrossfadeEffect: function (imageContainer, bottleImage, backgroundElement) {
    const bottleShadow = imageContainer.querySelector('.product-card__image--shadow img');
    gsap.set(backgroundElement, { opacity: 0 });

    const tl = gsap.timeline({ paused: true });

    // Single timeline for both directions
    tl.to([bottleImage, bottleShadow], { opacity: 0, duration: 0.75, ease: 'power1.out' }, 0).to(backgroundElement, { opacity: 1, duration: 0.75, ease: 'power1.out' }, '-=0.75');

    let isHovering = false;
    let animationProgress = 0;

    const updateAnimation = () => {
      gsap.to(tl, {
        progress: isHovering ? 1 : 0,
        duration: 0.5,
        ease: 'power2.inOut',
        onUpdate: () => {
          animationProgress = tl.progress();
        },
      });
    };

    imageContainer.addEventListener('mouseenter', () => {
      isHovering = true;
      updateAnimation();
    });

    imageContainer.addEventListener('mouseleave', () => {
      isHovering = false;
      updateAnimation();
    });
  },

  insertCtaCards: function () {
    console.log('Attempting to insert CTA cards');
    if (typeof ctaCardsData === 'undefined' || !ctaCardsData.length) {
      console.log('No CTA card data available');
      return;
    }

    const gridContainer = document.querySelector('.shop-all-inner-cntr.c7-product-collection');
    if (!gridContainer) {
      console.log('Grid container not found');
      return;
    }

    const productCards = gridContainer.querySelectorAll('.product-card');
    if (productCards.length === 0) {
      console.log('No product cards found in the grid. Commerce7 might not have finished loading.');
      return;
    }

    //   console.log(`Found ${productCards.length} product cards`);

    // Sort CTA cards by their insert_after_product value
    ctaCardsData.sort((a, b) => {
      const aPosition = parseInt(this.getInsertAfterProduct(a)) || Infinity;
      const bPosition = parseInt(this.getInsertAfterProduct(b)) || Infinity;
      return aPosition - bPosition;
    });

    let productCount = 0;
    const children = Array.from(gridContainer.children);

    children.forEach((child, index) => {
      if (child.classList.contains('product-card')) {
        productCount++;
      }

      ctaCardsData.forEach((card, cardIndex) => {
        const insertAfter = parseInt(this.getInsertAfterProduct(card));
        if (productCount === insertAfter) {
          console.log(`Inserting CTA card after product ${productCount}`);
          const cardElement = this.createCtaCardElement(card);
          gridContainer.insertBefore(cardElement, children[index + 1]);
          ctaCardsData.splice(cardIndex, 1); // Remove the inserted card from the array
        }
      });
    });

    // Insert any remaining cards at the end
    ctaCardsData.forEach((card) => {
      console.log('Appending remaining CTA card to the end');
      const cardElement = this.createCtaCardElement(card);
      gridContainer.appendChild(cardElement);
    });

    console.log('Finished inserting CTA cards');
  },

  getInsertAfterProduct: function (card) {
    return card.card_type === 'one-column' ? card.single_column_card.insert_after_product : card.double_column_card.insert_after_product;
  },

  createCtaCardElement: function (card) {
    const cardElement = document.createElement('div');

    if (card.card_type === 'one-column') {
      const cardData = card.single_column_card;
      const bgImage = cardData.background_image && cardData.background_image.url ? cardData.background_image.url : '';

      cardElement.className = 'cta-card single-column';
      cardElement.innerHTML = `
          <div class="cta-card-bg" style="background-image: url('${bgImage}')"></div>
          <div class="cta-card-content">
              <h3 class="cta-card-title">${cardData.title}</h3>
              <p class="cta-card-copy">${cardData.copy}</p>
              <p class="cta-card-footer">${cardData.footer_copy}</p>
          </div>
      `;
    } else if (card.card_type === 'two-column') {
      const cardData = card.double_column_card;
      const bgImage = cardData.background_image && cardData.background_image.url ? cardData.background_image.url : '';
      const link = cardData.cta_link;

      cardElement.className = 'cta-card double-column';
      cardElement.innerHTML = `
          <div class="cta-card-bg" style="background-image: url('${bgImage}')"></div>
          <div class="cta-card-content">
              <h3 class="cta-card-title">${cardData.title}</h3>
              <p class="cta-card-copy">${cardData.copy}</p>
              ${link ? `<a href="${link.url}" class="cta-card-link rmbr-pill rmbr-pill--arrow white-variant">${link.title}</a>` : ''}
          </div>
      `;
    } else {
      console.error('Unknown card type:', card.card_type);
      return null;
    }

    return cardElement;
  },

  waitForC7ProductsToLoad: function () {
    console.log('Checking for Commerce7 products');

    // Check if we have preloaded data from server
    if (window.preloadedC7Products && window.preloadedC7Products.products) {
      console.log(
        '%c✅ USING CACHED DATA: Found preloaded Commerce7 products from WordPress transient cache',
        'background: #e7f5ea; color: #0a6b2d; padding: 5px; border-radius: 3px; font-weight: bold;'
      );
      console.log('Cache contains ' + window.preloadedC7Products.products.length + ' products');

      // Log cache timing information if available
      if (document.querySelector('.c7-cache-debug')) {
        const cacheInfo = document.querySelector('.c7-cache-debug').innerText;
        console.log('Cache details:', cacheInfo);
      }

      // Process the preloaded products
      this.processPreloadedProducts(window.preloadedC7Products);

      // Add a performance marker
      if (window.performance && window.performance.mark) {
        window.performance.mark('c7-products-from-cache-loaded');
      }

      return;
    }

    // Fallback to original loading method if no preloaded data
    console.log('%c⚠️ NO CACHE FOUND: Falling back to Commerce7 API', 'background: #fff3cd; color: #856404; padding: 5px; border-radius: 3px; font-weight: bold;');
    console.log('This may be slower. Consider checking if the cache is working properly.');

    let attempts = 0;
    const maxAttempts = 20; // Adjust as needed
    const checkInterval = 500; // Check every 500ms

    const checkForProducts = () => {
      const gridContainer = document.querySelector('.shop-all-inner-cntr.c7-product-collection');
      const productCards = gridContainer ? gridContainer.querySelectorAll('.product-card') : [];

      if (productCards.length > 0) {
        console.log(`Commerce7 products loaded. Found ${productCards.length} products.`);
        // Add 'loaded' class to hide the skeleton
        if (gridContainer) {
          gridContainer.classList.add('loaded');
        }
        // Also add a class to the body to hide the loading overlay
        document.body.classList.add('c7-products-loaded');
        document.body.classList.remove('c7-loading');

        // Create and dispatch a custom event
        const productsLoadedEvent = new CustomEvent('c7-products-loaded', {
          detail: { productCount: productCards.length },
        });
        document.dispatchEvent(productsLoadedEvent);

        // Ensure Commerce7 components are properly initialized
        if (window.c7 && typeof window.c7.initComponents === 'function') {
          console.log('%c🔄 Reinitializing Commerce7 components for direct-loaded products', 'background: #e7f5ea; color: #0a6b2d; padding: 5px; border-radius: 3px; font-weight: bold;');
          window.c7.initComponents();
        }

        // Run post-processing after a short delay to ensure Commerce7 has finished its work
        setTimeout(() => {
          this.insertCtaCards();
          this.setupProductCardBackgroundAnimation();
          this.addNotifyMeCTA(productCards);

          // Fix any formatting issues that might have occurred
          this.fixProductFormatting(productCards);
        }, 500);
      } else if (attempts < maxAttempts) {
        attempts++;
        console.log(`Attempt ${attempts}: No products found yet. Trying again in ${checkInterval}ms.`);
        setTimeout(checkForProducts, checkInterval);
      } else {
        console.log('Max attempts reached. Commerce7 products might not have loaded properly.');

        // Add 'loaded' class even if no products found to hide the skeleton after max attempts
        const gridContainer = document.querySelector('.shop-all-inner-cntr.c7-product-collection');
        if (gridContainer) {
          gridContainer.classList.add('loaded');

          // Show a friendly message if no products were found
          if (gridContainer.children.length === 0) {
            const noProductsMessage = document.createElement('div');
            noProductsMessage.className = 'c7-no-products-message';
            noProductsMessage.innerHTML = `
              <div style="text-align: center; padding: 50px 20px; background: #f9f9f9; border-radius: 8px; margin: 30px 0;">
                <h3 style="margin-bottom: 15px; color: #333;">No products found in this collection</h3>
                <p style="margin-bottom: 20px; color: #666;">We couldn't find any products in this collection right now.</p>
                <p style="color: #666;">Please try another collection or check back later.</p>
                <a href="/shop" style="display: inline-block; margin-top: 20px; padding: 10px 20px; background: #8b0000; color: white; text-decoration: none; border-radius: 4px;">Browse All Wines</a>
              </div>
            `;
            gridContainer.appendChild(noProductsMessage);
          }
        }

        // Also add a class to the body to hide the loading overlay
        document.body.classList.add('c7-products-loaded');
        document.body.classList.remove('c7-loading');

        // Log an error to the console for debugging
        console.error('No Commerce7 products were found for this collection. Please check the following:');
        console.error('1. Is the collection slug correct?');
        console.error('2. Does the collection contain any products?');
        console.error('3. Are the API credentials correct?');
        console.error('4. Check the browser network tab for API errors');
      }
    };

    checkForProducts();
  },

  processPreloadedProducts: function (productsData) {
    const gridContainer = document.querySelector('.shop-all-inner-cntr.c7-product-collection');
    if (!gridContainer) {
      console.error('Grid container not found');
      return;
    }

    // Create product cards from the preloaded data
    if (productsData.products && productsData.products.length > 0) {
      productsData.products.forEach((product) => {
        const card = this.createProductCard(product);
        if (card) {
          gridContainer.appendChild(card);
        }
      });

      // Hide the skeleton loader
      document.body.classList.add('c7-products-loaded');
      document.body.classList.remove('c7-loading');
      gridContainer.classList.add('loaded');

      // Create and dispatch a custom event
      const productsLoadedEvent = new CustomEvent('c7-products-loaded', {
        detail: { productCount: productsData.products.length },
      });
      document.dispatchEvent(productsLoadedEvent);

      // IMPORTANT: Initialize Commerce7 components
      // This ensures the Commerce7 JavaScript library properly connects with our cached products
      console.log('%c🔄 Initializing Commerce7 components for cached products', 'background: #e7f5ea; color: #0a6b2d; padding: 5px; border-radius: 3px; font-weight: bold;');

      // Wait for Commerce7 library to be fully loaded
      const initC7Components = () => {
        if (window.c7 && typeof window.c7.initComponents === 'function') {
          console.log('Commerce7 library loaded, initializing components');
          window.c7.initComponents();

          // Run post-processing functions after C7 initialization
          setTimeout(() => {
            this.insertCtaCards();
            this.setupProductCardBackgroundAnimation();
            this.processProductCards(gridContainer.querySelectorAll('.product-card'));
            this.addNotifyMeCTA(gridContainer.querySelectorAll('.product-card'));
            // Fix any formatting issues that might have occurred
            this.fixProductFormatting(gridContainer.querySelectorAll('.product-card'));
          }, 500);
        } else {
          console.log('Waiting for Commerce7 library to load...');
          setTimeout(initC7Components, 100);
        }
      };

      initC7Components();
    } else {
      console.error('No products found in preloaded data');

      // Hide the skeleton loader even if no products
      document.body.classList.add('c7-products-loaded');
      document.body.classList.remove('c7-loading');
      gridContainer.classList.add('loaded');

      // Show a friendly message if no products were found
      const noProductsMessage = document.createElement('div');
      noProductsMessage.className = 'c7-no-products-message';
      noProductsMessage.innerHTML = `
        <div style="text-align: center; padding: 50px 20px; background: #f9f9f9; border-radius: 8px; margin: 30px 0;">
          <h3 style="margin-bottom: 15px; color: #333;">No products found in this collection</h3>
          <p style="margin-bottom: 20px; color: #666;">We couldn't find any products in this collection right now.</p>
          <p style="color: #666;">Please try another collection or check back later.</p>
          <a href="/shop" style="display: inline-block; margin-top: 20px; padding: 10px 20px; background: #8b0000; color: white; text-decoration: none; border-radius: 4px;">Browse All Wines</a>
        </div>
      `;
      gridContainer.appendChild(noProductsMessage);

      // Log detailed error information
      console.error('No products found in preloaded data. Details:');
      console.error('Products data structure:', productsData);
      console.error('Collection slug:', gridContainer.getAttribute('data-collection-slug'));
    }
  },

  // Add this new method
  addNotifyMeCTA: function (productCards) {
    productCards.forEach((card) => {
      const subtitle = card.querySelector('.product-card__subtitle');
      const footer = card.querySelector('.product-card__footer');

      if ((subtitle && subtitle.textContent.toLowerCase().includes('sold out')) || (footer && footer.children.length === 0)) {
        const notifyMeButton = document.createElement('button');
        notifyMeButton.className = 'rmbr-pill';
        notifyMeButton.textContent = 'Notify Me';

        // Extract product information
        const productTitle = card.querySelector('.product-card__title')?.textContent.trim() || '';
        const productVintage = card.querySelector('.product-card__vintage')?.textContent.trim() || '';
        const productUrl = card.querySelector('a')?.href || '';

        notifyMeButton.addEventListener('click', (e) => {
          e.preventDefault();

          // Populate hidden fields in the Gravity Form
          const titleVintageField = document.querySelector('#field_2_7 input');
          const urlField = document.querySelector('#field_2_8 input');

          if (titleVintageField) {
            titleVintageField.value = `${productTitle} ${productVintage}`.trim();
          }
          if (urlField) {
            urlField.value = productUrl;
          }

          // Open the micromodal
          microModal.show('notify-me-modal');
        });

        if (footer) {
          footer.appendChild(notifyMeButton);
        } else {
          const newFooter = document.createElement('div');
          newFooter.className = 'product-card__footer';
          newFooter.appendChild(notifyMeButton);
          card.querySelector('.product-card__content').appendChild(newFooter);
        }
      }
    });
  },

  insertCollectionTitle: function () {
    const path = window.location.pathname;
    if (path.includes('/collection/')) {
      const collectionName = path.split('/collection/')[1];
      if (collectionName) {
        const formattedName = collectionName
          .replace(/-|_/g, ' ') // Replace hyphens and underscores with spaces
          .replace(/\b\w/g, (l) => l.toUpperCase()); // Capitalize first letter of each word

        const container = document.querySelector('.general-collection__grid .container');
        if (container) {
          const titleElement = document.createElement('h1');
          titleElement.className = 'collection-title';
          titleElement.textContent = formattedName;
          container.insertBefore(titleElement, container.firstChild);
        }
      }
    }
  },

  setupProductCardBackgroundAnimation: function () {
    console.log('Setting up product card background animation');

    // Create a matchMedia instance
    let mm = gsap.matchMedia();

    // Use GSAP matchMedia to apply the effect only on screens smaller than 550px
    mm.add('(max-width: 549px)', (context) => {
      // Select all product cards
      const productCards = document.querySelectorAll('.product-card');
      const animations = []; // Store animations for cleanup

      productCards.forEach((card) => {
        const imageContainer = card.querySelector('.product-card__image-container');
        const bottleImage = card.querySelector('.product-card__image:not(.product-card__image--shadow) img');
        const bottleShadow = card.querySelector('.product-card__image--shadow img');
        const background = card.querySelector('.product-card__background');

        // Check if all required elements exist and if the background has an inline background-image
        if (imageContainer && bottleImage && bottleShadow && background && background.style.backgroundImage) {
          // Create a timeline for the crossfade effect
          const tl = gsap.timeline({ paused: true });

          tl.to([bottleImage, bottleShadow], { opacity: 0, duration: 0.75, ease: 'power2.inOut' }, 0).to(background, { opacity: 1, duration: 0.75, ease: 'power2.inOut' }, '-=0.3');

          const st = ScrollTrigger.create({
            trigger: card,
            start: 'top 30%', // Updated start value
            end: 'bottom 70%', // Updated end value
            onEnter: () => tl.play(),
            onLeave: () => tl.reverse(),
            onEnterBack: () => tl.play(),
            onLeaveBack: () => tl.reverse(),
          });

          animations.push({ tl, st }); // Store both timeline and ScrollTrigger
        }
      });

      // Return a cleanup function
      return () => {
        animations.forEach(({ tl, st }) => {
          tl.kill();
          st.kill();
        });
      };
    });
  },
  setupHeroScrollMessage: function () {
    const hero = document.querySelector('.shop-all-hero');
    if (!hero) return;

    // Create the scroll message element
    const scrollMessage = document.createElement('div');
    scrollMessage.textContent = 'Scroll to Discover';
    scrollMessage.style.cssText = `
      position: fixed;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.3s ease;
      background-color: rgba(255, 255, 255, 0.8);
      color: rgb(38, 172, 235);
      padding: 15px 20px;
      border-radius: 20px;
      font-size: 14px;
      font-weight: 400;
      z-index: 1000;
      letter-spacing: 0.05em;
      font-family: ivymode, "sans-serif";
    `;
    document.body.appendChild(scrollMessage);

    // Function to update message position
    const updateMessagePosition = (e) => {
      scrollMessage.style.left = `${e.clientX + 20}px`;
      scrollMessage.style.top = `${e.clientY - 20}px`;
    };

    // Function to show message
    const showMessage = () => {
      scrollMessage.style.opacity = '1';
    };

    // Function to hide message
    const hideMessage = () => {
      scrollMessage.style.opacity = '0';
    };

    // Get the CTA element
    const cta = hero.querySelector('.shop-all-hero__cta');

    // Show message on hover, except when over CTA
    hero.addEventListener('mouseenter', () => {
      hero.addEventListener('mousemove', (e) => {
        updateMessagePosition(e);

        // Check if the mouse is over the CTA
        const isOverCTA = cta && cta.contains(e.target);

        if (isOverCTA) {
          hideMessage();
        } else {
          showMessage();
        }
      });
    });

    // Hide message when not hovering over hero
    hero.addEventListener('mouseleave', hideMessage);
  },

  setHeroHeight: function () {
    const hero = document.querySelector('.shop-all-hero');
    if (!hero) {
      return;
    }

    const header = document.querySelector('.rmbr.grid__header');
    if (!header) {
      return;
    }

    // Check if header has a height
    if (header.offsetHeight === 0) {
      console.log('⏳ Header height is 0, waiting for render...');
      setTimeout(() => this.setHeroHeight(), 100);
      return;
    }

    // Store matchMedia instance as a property if it doesn't exist
    if (!this.mm) {
      this.mm = gsap.matchMedia();
    } else {
      console.log('♻️ Using existing matchMedia instance');
    }

    // Clear any existing media queries
    this.mm.revert();

    // Save initial styles
    gsap.set(hero, { clearProps: 'height' });

    // Add the media queries
    this.mm.add(
      {
        isDesktop: '(min-width: 992px)',
        isMobile: '(max-width: 991px)',
      },
      (context) => {
        const { isDesktop } = context.conditions;

        if (isDesktop) {
          const headerHeight = header.offsetHeight;
          const windowHeight = window.innerHeight;
          const heroHeight = windowHeight - headerHeight;

          gsap.set(hero, { height: heroHeight });
          hero.classList.add('custom-height-applied');
        } else {
          gsap.set(hero, { clearProps: 'height' });
          hero.classList.remove('custom-height-applied');
        }

        // Return cleanup function
        return () => {
          gsap.set(hero, { clearProps: 'height' });
          hero.classList.remove('custom-height-applied');
        };
      }
    );
  },

  // Add this new method to fix product formatting issues
  fixProductFormatting: function (productCards) {
    console.log('Fixing product formatting issues');

    productCards.forEach((card) => {
      // Fix variant title formatting (remove triple parentheses)
      const variantTitle = card.querySelector('.c7-product__variant__price__title');
      if (variantTitle) {
        let text = variantTitle.textContent.trim();
        // Remove any existing parentheses
        text = text.replace(/^\(+|\)+$/g, '');
        // Add back a single set of parentheses
        variantTitle.textContent = `(${text})`;
      }

      // Ensure "Add to Cart" button has the correct data attribute
      const addToCartBtn = card.querySelector('button:not([data-c7-action])');
      if (addToCartBtn && addToCartBtn.textContent.includes('Add to Cart')) {
        addToCartBtn.setAttribute('data-c7-action', 'add-to-cart');
      }
    });
  },
};

export default Shop;
